import { set } from "@ember/object"
import Service, { inject as service } from "@ember/service"
import { isNone } from "@ember/utils"
import * as Sentry from "@sentry/browser"
import { cloneCardStackAttributesWithNewIds } from "@shared/lib/card-stack/clone"
import { mergeFollowedLessonContent } from "@shared/lib/card-stack/merge-followed-lesson"
import {
  emptyProseMirrorContent,
  nonInstructionalNoteInitialContent,
} from "@shared/lib/card-stack/prose-mirror-utils"
import type {
  Card,
  CardAttachment,
  CardGoogleClassroom,
  CardStack,
  CardStackAttributes,
  CardStandard,
  GoogleClassroomClassSetting,
  GoogleClassroomPostType,
  LegacyCardComment,
  LegacyCardStackUsedAsTypes,
  LessonCard,
  ParentCardStack,
  ProseMirrorContent,
  ShareModeSettings,
  UnifiedCardStack,
} from "@shared/models/card-stack"
import { CARD_COLORS } from "@shared/models/card-stack"
import type { Comment, CommentRecipient } from "@shared/models/comment"
import type { Course, CourseCalendarTime } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import type { User } from "@shared/models/user"
import ObjectId from "@shared/util/object-id-gen"
import downloadAttachmentAction from "cc-frontend/lib/actions/attachments/DOWNLOAD_ATTACHMENT"
import { prepareCourseSetOneTimeAction } from "cc-frontend/lib/actions/course/calendar/COURSE_SET_ONE_TIME"
import { prepareDeleteUnit } from "cc-frontend/lib/actions/course/calendar/DELETE_UNIT"
import prepareGCSettingsUpdate from "cc-frontend/lib/actions/lesson/GC_SETTINGS_UPDATE"
import googleClassroomLessonCardUpdate from "cc-frontend/lib/actions/lesson/GOOGLE_CLASSROOM_LESSON_CARD_UPDATE"
import { lessonEditIds } from "cc-frontend/lib/actions/lesson/lesson-edit-ids"
import { prepareLessonAiChange } from "cc-frontend/lib/actions/lesson/LESSON_AI_CHANGE"
import { prepareUpsertShareMode } from "cc-frontend/lib/actions/lesson/LESSON_CARD_GC_CHANGE_SHARE_MODE"
import prepareGCScheduleClass from "cc-frontend/lib/actions/lesson/LESSON_CARD_GC_SCHEDULE_CLASS"
import prepareGCUnscheduleClass from "cc-frontend/lib/actions/lesson/LESSON_CARD_GC_UNSCHEDULE_CLASS"
import { LESSON_CARD_INTERMEDIATE_UPDATE } from "cc-frontend/lib/actions/lesson/LESSON_CARD_INTERMEDIATE_UPDATE"
import LESSON_CARD_REMOVE_COMMENT from "cc-frontend/lib/actions/lesson/LESSON_CARD_REMOVE_COMMENT"
import LESSON_CARD_RENAME_ATTACHMENT from "cc-frontend/lib/actions/lesson/LESSON_CARD_RENAME_ATTACHMENT"
import { prepareLessonCopyFollowedLesson } from "cc-frontend/lib/actions/lesson/LESSON_COPY_FOLLOWED_LESSON"
import { prepareCreateUnifiedNote } from "cc-frontend/lib/actions/lesson/LESSON_CREATE_UNIFIED_NOTE"
import { prepareLessonDeleteComment } from "cc-frontend/lib/actions/lesson/LESSON_DELETE_COMMENT"
import { prepareLessonErase } from "cc-frontend/lib/actions/lesson/LESSON_ERASE"
import { preparePasteLesson } from "cc-frontend/lib/actions/lesson/LESSON_PASTE"
import LESSON_STANDARDS_EMPHASIS from "cc-frontend/lib/actions/lesson/LESSON_STANDARDS_EMPHASIS"
import { prepareClearAllLessonsForDay } from "cc-frontend/lib/actions/planbook/lessons/PLANBOOK_CLEAR_ALL_LESSONS_FOR_DAY"
import { preparePasteAllLessonsForDay } from "cc-frontend/lib/actions/planbook/lessons/PLANBOOK_PASTE_ALL_LESSONS_FOR_DAY"
import { getCardStackIdForDate } from "cc-frontend/lib/unit-utils"
import moment from "cc-frontend/utils/extended-moment"
import { filterCourseDateCustom } from "cc-frontend/utils/filter-dates/course"
import { task, timeout } from "ember-concurrency"
import _, { isNil } from "lodash"
import { cloneDeep, find, isArray, isEqual, last, map, max, reject, sortBy } from "lodash-es"
import { COURSE_UPDATE_STANDARD_SET_IDS } from "../lib/actions/course/COURSE_UPDATE_STANDARD_SET_IDS"
import { LESSON_CARD_ADD } from "../lib/actions/lesson/LESSON_CARD_ADD"
import { LESSON_CARD_ADD_ATTACHMENTS } from "../lib/actions/lesson/LESSON_CARD_ADD_ATTACHMENTS"
import lessonCardAddCommentAction from "../lib/actions/lesson/LESSON_CARD_ADD_COMMENT"
import { LESSON_CARD_DELETE } from "../lib/actions/lesson/LESSON_CARD_DELETE"
import { LESSON_CARD_MARK_PUBLIC } from "../lib/actions/lesson/LESSON_CARD_MARK_PUBLIC"
import { LESSON_CARD_MOVE_TO_NEW_LESSON } from "../lib/actions/lesson/LESSON_CARD_MOVE_TO_NEW_LESSON"
import { LESSON_CARD_MOVE_WITHIN_LESSON } from "../lib/actions/lesson/LESSON_CARD_MOVE_WITHIN_LESSON"
import lessonCardPasteAction from "../lib/actions/lesson/LESSON_CARD_PASTE"
import { LESSON_CARD_REMOVE_ATTACHMENT } from "../lib/actions/lesson/LESSON_CARD_REMOVE_ATTACHMENT"
import { LESSON_CARD_REMOVE_LEGACY_COMMENT } from "../lib/actions/lesson/LESSON_CARD_REMOVE_LEGACY_COMMENT"
import { prepareLessonCardUpdate } from "../lib/actions/lesson/LESSON_CARD_UPDATE"
import { prepareLessonClear } from "../lib/actions/lesson/LESSON_CLEAR"
import editLessonTitleAction from "../lib/actions/lesson/LESSON_EDIT_TITLE"
import { LESSON_FIRST_CARDS_EDIT } from "../lib/actions/lesson/LESSON_FIRST_CARDS_EDIT"
import { LESSON_FIRST_EDIT } from "../lib/actions/lesson/LESSON_FIRST_EDIT"
import { LESSON_STANDARDS_DELETE } from "../lib/actions/lesson/LESSON_STANDARDS_DELETE"
import lessonStandardsReplaceAction from "../lib/actions/lesson/LESSON_STANDARDS_REPLACE"
import { LESSON_TITLE_INTERMEDIATE_UPDATE } from "../lib/actions/lesson/LESSON_TITLE_INTERMEDIATE_UPDATE"
import lessonUnifiedCardAddCommentAction from "../lib/actions/lesson/LESSON_UNIFIED_CARD_ADD_COMMENT"
import { LESSON_UNIFIED_INTERMEDIATE_UPDATE } from "../lib/actions/lesson/LESSON_UNIFIED_INTERMEDIATE_UPDATE"
import { prepareLessonUnifiedUpdate } from "../lib/actions/lesson/LESSON_UNIFIED_UPDATE"
import { PLANBOOK_UPDATE_RECENT_STANDARD_SETS } from "../lib/actions/planbook/PLANBOOK_UPDATE_RECENT_STANDARD_SETS"
import reorderPositionedList from "../lib/reorder-positioned-list"
import type Dispatcher from "../services/dispatcher"
import type EmberStore from "../services/ember-store"
import type Session from "./session"
import type SocketService from "./socket"

interface LessonsForCourses {
  courseId: string
  cardStackAttrs: CardStackAttributes
}

/**
 * Models needed for the first edit to a lesson.
 */
export type LessonModelsNeededForFirstEdit = {
  cardStack: CardStack & { attributes: { usedAs: "lesson" } }
  dateString: string
  planbook: Planbook
  course: Course
  rotationCalendar: RotationCalendar
}

/**
 * Models needed for the first edit to a lesson, or an indicator that they are not needed
 * (for units and templates).
 */
export type ModelsNeededForFirstEdit =
  | LessonModelsNeededForFirstEdit
  | {
      // Allow passing a trivial ModelsNeededForFirstEdit, but only if you confirm
      // that you are not working with a lesson.
      cardStack: CardStack & {
        attributes: { usedAs: Exclude<LegacyCardStackUsedAsTypes, "lesson"> }
      }
    }

export function isLessonModelsNeededForFirstEdit(
  models: ModelsNeededForFirstEdit,
): models is LessonModelsNeededForFirstEdit {
  return models.cardStack.attributes.usedAs === "lesson"
}

export default class LessonEditor extends Service {
  @service("ember-store") declare store: EmberStore
  @service declare dispatcher: Dispatcher
  @service declare session: Session
  @service declare socket: SocketService

  /*====================================================================================================
  PUBLIC METHODS
  ==================================================================================================== */

  /*----------------------------------------------------------------------------------------------------
  Edit cards
  ---------------------------------------------------------------------------------------------------- */
  editTitle(
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit,
    cardStackId: string,
    value: string,
    setCards: boolean,
  ) {
    this._createLessonIfNeeded(modelsNeededForFirstEdit, setCards)
    // @ts-ignore
    this._intermediateLessonTitleUpdate.perform({
      cardStackId: cardStackId,
      value: value,
    })
  }

  finishEditingTitle(
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit,
    cardStackId: string,
    value: string | null,
    originalValue: string | null,
  ) {
    this._createLessonIfNeeded(modelsNeededForFirstEdit, false)

    // @ts-ignore
    this._intermediateLessonTitleUpdate.cancelAll()

    if ((value === "" && originalValue === null) || value === originalValue) return

    this.dispatcher.dispatchPreparedAction(
      editLessonTitleAction({
        cardStackId,
        value: value,
        originalValue: originalValue,
      }),
    )
  }

  setCustomTime(
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit,
    course: Course,
    dateString: string,
    planbookId: string,
    time: CourseCalendarTime | null,
  ) {
    this._createLessonIfNeeded(modelsNeededForFirstEdit, false)

    const courseDate = filterCourseDateCustom(course).find(
      (date) => date.attributes.dateString === dateString,
    )
    if (courseDate === undefined) return
    this.dispatcher.dispatchPreparedAction(
      prepareCourseSetOneTimeAction({
        course,
        courseDate,
        planbookId,
        time,
      }),
    )
  }

  editCard(
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit,
    cardStackId: string,
    id: string,
    prop: string,
    value,
  ) {
    this._createLessonIfNeededAndCardsFromTemplate(modelsNeededForFirstEdit)
    // @ts-ignore
    this._intermediateLessonCardUpdate.perform({ id, prop, value, cardStackId })
  }

  finishEditingCard(
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit,
    cardStackId: string,
    id: string,
    prop: string,
    value: string | null,
    originalValue: string | null,
    wordCountChange: number | null,
  ) {
    // @ts-ignore
    this._intermediateLessonCardUpdate.cancelAll()
    this._createLessonIfNeededAndCardsFromTemplate(modelsNeededForFirstEdit)

    this.dispatcher.dispatchPreparedAction(
      prepareLessonCardUpdate({
        cardStackId: cardStackId,
        sectionId: id,
        prop: prop,
        value: value,
        originalValue: originalValue,
        wordCountChange: wordCountChange || 0,
      }),
    )
  }

  editUnifiedLesson(
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit,
    cardStackId: string,
    proseMirror: ProseMirrorContent,
  ) {
    this._createLessonIfNeededAndCardsFromTemplate(modelsNeededForFirstEdit)
    this.dispatcher.dispatchAction(LESSON_UNIFIED_INTERMEDIATE_UPDATE, {
      cardStackId,
      ...lessonEditIds(modelsNeededForFirstEdit),
      proseMirror,
    })
  }

  /**
   * @returns The `original` field corresponding to the value after this edit.
   */
  finishEditingUnifiedLesson(
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit,
    cardStackId: string,
    proseMirror: ProseMirrorContent,
    originalProseMirror: ProseMirrorContent,
    /** Who wrote this version: the user in the editor, or the AI panel's accepted edits. */
    source: "user" | "ai",
  ): void {
    this._createLessonIfNeededAndCardsFromTemplate(modelsNeededForFirstEdit)

    this.dispatcher.dispatchPreparedAction(
      prepareLessonUnifiedUpdate({
        cardStackId,
        ...lessonEditIds(modelsNeededForFirstEdit),
        proseMirror,
        originalProseMirror,
        source,
      }),
    )
  }

  /**
   * Creates a note in a non-instructional course's slot (unified version).
   */
  createUnifiedNote(modelsNeededForFirstEdit: ModelsNeededForFirstEdit, cardStackId: string): void {
    this._createLessonIfNeededAndCardsFromTemplate(modelsNeededForFirstEdit)

    const { content, cardId } = nonInstructionalNoteInitialContent()
    this.dispatcher.dispatchPreparedAction(
      prepareCreateUnifiedNote({
        cardStackId,
        ...lessonEditIds(modelsNeededForFirstEdit),
        cardId,
        proseMirror: content,
      }),
    )
  }

  finishEditingGoogleClassroomCard(
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit,
    cardStackId: string,
    id: string,
    prop: "title" | "value" | "maxPoints",
    value: string | null,
    originalValue: string | null,
    wordCountChange: any,
  ) {
    // @ts-ignore
    this._intermediateLessonCardUpdate.cancelAll()
    this._createLessonIfNeededAndCardsFromTemplate(modelsNeededForFirstEdit)

    this.dispatcher.dispatchPreparedAction(
      googleClassroomLessonCardUpdate({
        cardStackId: cardStackId,
        cardId: id,
        prop: prop,
        value: value,
        originalValue: originalValue,
        wordCountChange: wordCountChange || 0,
      }),
    )
  }

  pasteCard(
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit,
    cardStackId: string,
    cardId: string,
    cardToReplace: Card,
    cardToPaste: Card,
  ) {
    this._createLessonIfNeededAndCardsFromTemplate(modelsNeededForFirstEdit)
    this.dispatcher.dispatchPreparedAction(
      lessonCardPasteAction({
        cardStackId: cardStackId,
        cardToReplaceId: cardId,
        cardToReplace: cardToReplace,
        cardToPaste: cardToPaste,
      }),
    )
  }

  changeCardColor(
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit,
    color: string,
    cardStackId: string,
    cardId: string,
    originalValue: string | null,
  ) {
    this._createLessonIfNeededAndCardsFromTemplate(modelsNeededForFirstEdit)
    this.dispatcher.dispatchPreparedAction(
      prepareLessonCardUpdate({
        cardStackId: cardStackId,
        sectionId: cardId,
        prop: "color",
        value: color,
        originalValue: originalValue,
      }),
    )
  }

  acceptAiChanges(
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit,
    cardStack: CardStack,
    changes: Map<
      string,
      Array<{
        prop: string
        originalValue: unknown
        value: unknown
      }>
    >,
    lessonTitle: string | undefined,
    originalLessonTitle: string | null,
    chatId: string,
    planbookId: string,
  ) {
    this._createLessonIfNeededAndCardsFromTemplate(modelsNeededForFirstEdit)

    this.dispatcher.dispatchPreparedAction(
      prepareLessonAiChange({
        cardStackId: cardStack.id,
        chatId,
        changes,
        lessonTitle,
        originalLessonTitle,
        planbookId,
      }),
    )
  }

  toggleClassWebsite(
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit,
    value: boolean,
    cardStackId: string,
    cardId: string,
  ) {
    this._createLessonIfNeededAndCardsFromTemplate(modelsNeededForFirstEdit)
    this.dispatcher.dispatchAction(LESSON_CARD_MARK_PUBLIC, {
      cardStackId: cardStackId,
      sectionId: cardId,
      value: value,
    })
  }

  scheduleGoogleClass(
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit,
    cardStackId: string,
    card: CardGoogleClassroom,
    classSetting: GoogleClassroomClassSetting,
    className: string,
    dateString: string,
  ) {
    // Enable the classSetting
    this._createLessonIfNeededAndCardsFromTemplate(modelsNeededForFirstEdit)
    classSetting.isEnabled = true

    this.dispatcher.dispatchPreparedAction(
      prepareGCScheduleClass({
        cardStackId: cardStackId,
        dateString: dateString,
        className: className,
        card: card,
        classSetting: classSetting,
      }),
    )
  }

  unscheduleGoogleClass(
    cardStackId: string,
    card: CardGoogleClassroom,
    classSetting: GoogleClassroomClassSetting,
    className: string,
    dateString: string,
  ) {
    classSetting.isEnabled = false
    this.dispatcher.dispatchPreparedAction(
      prepareGCUnscheduleClass({
        cardStackId: cardStackId,
        googleCardId: card.id,
        classSetting: classSetting,
        className: className,
        dateString: dateString,
      }),
    )
  }

  setGoogleClassroomTopic(
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit,
    // null clears the topic (the sidebar's topic picker allows clearing).
    topic: string | null,
    cardStackId: string,
    cardId: string,
  ) {
    this._createLessonIfNeededAndCardsFromTemplate(modelsNeededForFirstEdit)

    this.dispatcher.dispatchPreparedAction(
      googleClassroomLessonCardUpdate({
        prop: "topicName",
        value: topic,
        cardStackId: cardStackId,
        cardId: cardId,
      }),
    )
  }

  setMultipleChoiceQuestions(
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit,
    cardStackId: string,
    cardId: string,
    choices: Array<string>,
  ) {
    // Enable the classSetting
    this._createLessonIfNeededAndCardsFromTemplate(modelsNeededForFirstEdit)

    this.dispatcher.dispatchPreparedAction(
      googleClassroomLessonCardUpdate({
        prop: "choices",
        value: choices,
        cardStackId: cardStackId,
        cardId: cardId,
      }),
    )
  }

  /**
   * Updates the classSettings object of a CardGoogleClassroom
   */
  updateGoogleClass(
    cardStackId: string,
    card: CardGoogleClassroom,
    oldClassSetting: GoogleClassroomClassSetting,
    value: any,
    prop: string,
  ) {
    // Makes a copy of the old classSettings object
    let newClassSetting = cloneDeep(oldClassSetting)

    // replaces the value of the prop that need to be updated with the new, updated value,
    newClassSetting[prop] = value

    // removes the old classSettings object from the classSettings array on the GC lesson,
    let updatedClassSettings = reject(
      card.attributes.classSettings,
      (classSetting) => classSetting.googleClassId === oldClassSetting.googleClassId,
    )
    // and finally pushes the cloned, updated classSettings object into the classSettings array on the card
    updatedClassSettings.push(newClassSetting)

    // Create the action
    this.dispatcher.dispatchPreparedAction(
      prepareGCSettingsUpdate({
        cardStackId: cardStackId,
        googleCardId: card.id,
        oldGoogleClassSettings: card.attributes.classSettings,
        updatedClassSettings: updatedClassSettings,
        prop: prop,
      }),
    )
  }

  /*----------------------------------------------------------------------------------------------------
  Move & Add Cards
  ---------------------------------------------------------------------------------------------------- */

  addCardToBottom(
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit,
    cardStackId: string,
    id: string,
    type: "card-standards" | "card-title-value-html" | "card-google-classroom" | "card-value-html",
    postType?: GoogleClassroomPostType,
  ) {
    let cards = modelsNeededForFirstEdit.cardStack.attributes.cards
    this._createLessonIfNeededAndCardsFromTemplate(modelsNeededForFirstEdit)
    // should I notify if it's new?

    let cardsLength = isArray(cards) ? cards.length : 0

    let newPosition =
      cards === null || cardsLength === 0
        ? 0
        : // @ts-ignore:
          max(map(cards || [], (card) => card.attributes.position)) + 1000

    // Skip black as a generated color.
    const colorfulCardColors = CARD_COLORS.filter((color) => color !== "black")
    let colorIndex = cardsLength % colorfulCardColors.length
    this.dispatcher.dispatchAction(LESSON_CARD_ADD, {
      id: id,
      cardStackId: cardStackId,
      position: newPosition,
      color: colorfulCardColors[colorIndex],
      type: type,
      postType: postType,
    })
  }

  moveCard(
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit,
    movedCard: Card,
    oldCardStackId: string,
    newCardStackId: string,
    newParentCards: Array<Card>,
    receiverContext,
    newIndex: number,
  ) {
    let newCards = sortBy(newParentCards, "attributes.position")

    if (oldCardStackId === newCardStackId) {
      this._moveWithinLesson(
        modelsNeededForFirstEdit,
        movedCard,
        newCardStackId,
        newCards,
        newIndex,
      )
    } else {
      this._moveToNewLesson(
        modelsNeededForFirstEdit,
        movedCard,
        oldCardStackId,
        newCardStackId,
        receiverContext,
        newCards,
        newIndex,
      )
    }
  }

  /**
   * The drop event bubbles to the sender of the card, so we have to tell the receiver lesson
   * "hey there -- you're about to receive a card. If you're not ready for it, get ready"
   * Which means, it has to trigger the methods to submit the first edit and add cards from the template
   *
   */
  prepareToReceiveADraggedCard(modelsNeededForFirstEdit: ModelsNeededForFirstEdit) {
    this._createLessonIfNeeded(modelsNeededForFirstEdit, true)
    this._addCardsFromTemplate(modelsNeededForFirstEdit)
  }

  /* ----------------------------------------------------------------------------------------------------
    Adding things
    ---------------------------------------------------------------------------------------------------- */

  addComment(
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit,
    planbookId: string,
    cardStackId: string,
    card: LessonCard,
    commentText: string,
    commenter: User,
    recipients: CommentRecipient[],
    mentionedIds: string[],
  ) {
    this._createLessonIfNeededAndCardsFromTemplate(modelsNeededForFirstEdit)

    const commentId = ObjectId.create()
    this.store.insertDocument({
      type: "comment",
      id: commentId,
      attributes: {
        cachedReadStatuses: [],
        deletedAt: null,
        version: 0,
        _revision: null,
        _revisedBySessionId: null,
      },
      meta: { permissions: ["can-edit-comment", "can-view-comment"] },
    })

    this.dispatcher.dispatchPreparedAction(
      lessonCardAddCommentAction({
        planbookId,
        cardStackId,
        card,
        comment: {
          id: commentId,
          commentText,
          commenterId: commenter.id,
          recipients,
          mentionedIds,
        },
        commenterName: commenter.attributes.firstName + " " + commenter.attributes.lastName,
        commenterEmail: commenter.attributes.email,
      }),
    )
  }

  /**
   * Adds a comment to a card in a UnifiedCardStack.
   *
   * @param proseMirror The updated ProseMirrorContent including the commentId
   * in its card's attrs.commentIds.
   */
  addUnifiedCardComment(
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit,
    planbookId: string,
    cardStack: UnifiedCardStack,
    cardId: string,
    cardTitle: string | null,
    commentId: string,
    commentText: string,
    recipients: CommentRecipient[],
    mentionedIds: string[],
    proseMirror: ProseMirrorContent,
  ) {
    const commenter = this.session.user.content as User | null
    if (!commenter) return

    this._createLessonIfNeededAndCardsFromTemplate(modelsNeededForFirstEdit)

    this.store.insertDocument({
      type: "comment",
      id: commentId,
      attributes: {
        cachedReadStatuses: [],
        deletedAt: null,
        version: 0,
        _revision: null,
        _revisedBySessionId: null,
      },
      meta: { permissions: ["can-edit-comment", "can-view-comment"] },
    })
    this.socket.subscribe("comment", commentId)

    this.dispatcher.dispatchPreparedAction(
      lessonUnifiedCardAddCommentAction({
        planbookId,
        cardStackId: cardStack.id,
        cardId,
        cardTitle,
        comment: {
          id: commentId,
          commentText,
          commenterId: commenter.id,
          recipients,
          mentionedIds,
        },
        commenterName: commenter.attributes.firstName + " " + commenter.attributes.lastName,
        commenterEmail: commenter.attributes.email,
        proseMirror,
      }),
    )
  }

  addAttachments(
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit,
    _files,
    cardStackId: string,
    cardId: string,
    originalAttachments: Array<CardAttachment>,
    source: string,
  ) {
    let files = _.map(_files, (file) => {
      // @ts-ignore:
      set(file, "id", ObjectId.create())
      return file
    })
    this._createLessonIfNeededAndCardsFromTemplate(modelsNeededForFirstEdit)
    this.dispatcher.dispatchAction(LESSON_CARD_ADD_ATTACHMENTS, {
      cardStackId: cardStackId,
      originalAttachments: _.cloneDeep(originalAttachments),
      sectionId: cardId,
      source: source,
      files: files,
    })
  }

  upsertShareMode(
    attachmentId: string,
    shareMode: ShareModeSettings,
    cardStackId: string,
    cardId: string,
  ) {
    this.dispatcher.dispatchPreparedAction(
      prepareUpsertShareMode({
        cardStackId: cardStackId,
        cardId: cardId,
        attachmentId: attachmentId,
        shareMode: shareMode,
      }),
    )
  }

  pasteCardStack(
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit,
    cardStackId: string,
    /** The course containing the cardStack. */
    course: Course,
    /** The planbook containing the cardStack. */
    planbook: Planbook,
    newCardStackAttributes: CardStackAttributes,
    oldCardStackAttributes: CardStackAttributes,
    copiedFrom: string,
    parentCardStackInfo: ParentCardStack,
  ) {
    this._createLessonIfNeededAndCardsFromTemplate(modelsNeededForFirstEdit)
    const cloneAsUnified = !!planbook.attributes.flags.unifiedLessons
    let newAttributes = cloneCardStackAttributesWithNewIds(
      newCardStackAttributes,
      parentCardStackInfo,
      cloneAsUnified,
      {
        // When pasting a template, keep the existing lesson's title.
        overrideTitle: copiedFrom === "template" ? oldCardStackAttributes.title || "" : undefined,
        onMigrationError: (error) => Sentry.captureException(error),
      },
    )
    this.dispatcher.dispatchPreparedAction(
      preparePasteLesson({
        cardStackId: cardStackId,
        course,
        planbook,
        newLessonAttributes: newAttributes,
        originalLessonAttributes: cloneDeep(oldCardStackAttributes),
        copiedFrom: copiedFrom,
        parentCardStackInfo,
      }),
    )
  }

  copyFollowedLesson(
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit,
    cardStack: UnifiedCardStack,
    course: Course,
    planbook: Planbook,
    followedLesson: UnifiedCardStack,
    followedDateString: string,
  ) {
    const followedProseMirrorWithNewCardIds =
      cloneCardStackAttributesWithNewIds(followedLesson.attributes, null, true, {
        onMigrationError: (error: unknown) => Sentry.captureException(error),
      }).proseMirror ?? emptyProseMirrorContent()
    const originalProseMirror = cardStack.attributes.proseMirror ?? emptyProseMirrorContent()
    const proseMirror = mergeFollowedLessonContent(
      originalProseMirror,
      followedProseMirrorWithNewCardIds,
    )

    if (!isLessonModelsNeededForFirstEdit(modelsNeededForFirstEdit)) {
      throw new Error("A followed course's lesson can only be copied into a lesson")
    }

    const followedPlanbookId = followedLesson.relationships.planbook.data.id
    const followedPlanbook: Planbook | undefined = this.store.findInMemory(
      "planbook",
      followedPlanbookId,
    )

    this.dispatcher.dispatchPreparedAction(
      prepareLessonCopyFollowedLesson({
        cardStack,
        course,
        planbook,
        rotationCalendar: modelsNeededForFirstEdit.rotationCalendar,
        dateString: modelsNeededForFirstEdit.dateString,
        proseMirror,
        originalProseMirror,
        originalTitle: cardStack.attributes.title,
        followedLesson,
        followedDateString,
        parentCardStack: {
          id: followedLesson.id,
          version: followedLesson.attributes.version,
          ownerIds: followedPlanbook ? [followedPlanbook.relationships.owner.data.id] : null,
          courseId: followedLesson.relationships.course?.data?.id ?? null,
          planbookId: followedPlanbookId,
          source: "follow",
        },
        originalParentCardStack: cardStack.attributes.parentCardStack ?? null,
      }),
    )
  }

  /**
   * This pastes all lessons on the clipboard onto the specified day in the planbook.
   * First it loops through courses to find out which courses are on or off so as not to paste a lesson into a course that's off for the day.
   * When it declares cardStacks for courses, we must first find the oldCardStackId as well as the date.  Then we can assign new ids to the cards so there are no duplicates
   * Finally, we return the new cardstacks (and the old cardstackIds and cardstack, too) that we can give to the action as part of the payload
   */

  pasteAllLessonsForDay(
    dateString: string,
    planbook: Planbook,
    courses: Array<Course>,
    rotationCalendar: RotationCalendar,
    lessonsForCourses: Array<LessonsForCourses>,
  ) {
    let visibleCourses = _.filter(courses, (course) => {
      // filter classes that are turned off for the day
      let rotationId = _.chain(rotationCalendar.attributes.dates)
        .find((date) => date.attributes.dateString === dateString)
        // @ts-ignore
        .get("attributes.rotationId")
        .value()

      let defaultDateIsOff =
        _.find(course.attributes.calendar.datesOff, (date) => _.includes(date, rotationId)) !==
        undefined
      let customDateIsOff = _.includes(course.attributes.calendar.datesOff, dateString)
      let courseDateForcedOn = _.includes(course.attributes.calendar.datesForcedOn, dateString)

      let dateIsOff = defaultDateIsOff ? true : customDateIsOff

      return courseDateForcedOn ? true : dateIsOff !== true
    })

    let firstEditPatches = _.reduce(
      visibleCourses,
      (acc, course) => {
        let cardStackId = getCardStackIdForDate(course.attributes.calendar.dates, dateString)
        if (cardStackId === null || cardStackId === undefined) {
          let courseLessonObj = _.find(lessonsForCourses, { courseId: course.id })
          // if there are cards, we set the cards
          let setCards = courseLessonObj ? courseLessonObj.cardStackAttrs.cards !== null : false
          let newId = ObjectId.create()
          let payload = {
            course: course,
            planbook: planbook,
            rotationCalendar: rotationCalendar,
            dateString: dateString,
            // Pass empty content - it will be overwritten by later patches.
            // HACK: Only pass the fields that LESSON_FIRST_EDIT actually reads.
            cardStack: {
              id: newId,
              attributes: {
                schemaVersion: planbook.attributes.flags.unifiedLessons ? 2 : 1,
                cards: null,
                proseMirrorContent: null,
                extracted: null,
              },
            },
            setCards: setCards,
          }

          acc.courseIdToNewCardStackId[course.id] = newId
          // @ts-ignore no types  patch
          _.forEach(LESSON_FIRST_EDIT.patches(payload), (patch) => acc.patches.push(patch))
          // @ts-ignore no types  patch
          _.forEach(LESSON_FIRST_EDIT.undoPatches(payload), (patch) => acc.undoPatches.push(patch))
        }
        return acc
      },
      { patches: [], undoPatches: [], courseIdToNewCardStackId: {} },
    )

    let cardStacksForCourses = _.chain(courses)
      .map((course) => {
        let cardStackId = getCardStackIdForDate(course.attributes.calendar.dates, dateString)

        if (cardStackId === null || cardStackId === undefined) {
          cardStackId = firstEditPatches.courseIdToNewCardStackId[course.id]
        }
        let oldLesson = this.store.findInMemory("card-stack", cardStackId)
        let courseLessonObj = _.find(lessonsForCourses, { courseId: course.id })
        if (courseLessonObj) {
          let { cardStackAttrs, courseId } = courseLessonObj
          let lessonAttributes = cloneCardStackAttributesWithNewIds(
            cardStackAttrs,
            cardStackAttrs.parentCardStack,
            !!planbook.attributes.flags.unifiedLessons,
            { onMigrationError: (error) => Sentry.captureException(error) },
          )
          if (isNone(lessonAttributes)) return
          // We have the oldCardStack AND the oldCardStackId becuase if the lesson is new, we need to use the
          // lesson that we just created in the LESSON_FIRST_EDIT section above
          return {
            //string
            courseId: courseId,
            //CardStack
            oldCardStack: oldLesson,
            //string
            oldCardStackId: cardStackId,
            //CardStackAttributes
            newCardStackAttrs: lessonAttributes,
          }
        } else {
          return undefined
        }
      })
      .compact()
      .value()
    this.dispatcher.dispatchPreparedAction(
      preparePasteAllLessonsForDay({
        cardStacksForCourses: cardStacksForCourses,
        dateString: dateString,
        planbook: planbook,
        firstEditPatches: firstEditPatches.patches,
        firstEditUndoPatches: firstEditPatches.undoPatches,
        ownerIds: [planbook.relationships.owner.data.id],
        planbookId: planbook.id,
      }),
    )
  }

  /**
   * Creates a blank lesson in the given slot, if there is not a lesson there already.
   */
  createBlankLesson(models: ModelsNeededForFirstEdit) {
    this._createLessonIfNeededAndCardsFromTemplate(models)
  }

  /**
   * Makes the day's phantom a real lesson without filling it: `cards` stays null, so the lesson
   * goes on showing the day's template until something actually edits it — the island lands the
   * template on render (see `_addCardsFromTemplate`). This is what "Add Lesson" means: the day
   * gets a lesson of its own, and nobody has yet decided its contents.
   *
   * Not {@link createBlankLesson}, which copies the template's cards in. A copy is a decision:
   * change the day's template afterwards and the copy stays behind, showing the old one.
   */
  persistPhantomLesson(models: ModelsNeededForFirstEdit) {
    this._createLessonIfNeeded(models, false)
  }

  /*----------------------------------------------------------------------------------------------------
    Deleting Things
    ---------------------------------------------------------------------------------------------------- */

  // TODO This should be moved to the courseCalendarEditor since it acts on the course.calendar more than effects the lesson
  /**
    This removes the cardStack id from the courseDates so lessons can be bumped
    into its place.
  */
  eraseLesson(
    course: Course,
    dateString: string,
    cardStackId: string,
    time: CourseCalendarTime | null,
  ) {
    this.dispatcher.dispatchPreparedAction(
      prepareLessonErase({
        course: course,
        dateString: dateString,
        cardStackId: cardStackId,
        time,
      }),
    )
  }

  eraseUnit(
    unitId: string,
    course: Course,
    planbook: Planbook,
    rotationCalendar: RotationCalendar,
    shouldDeleteLessons: boolean,
  ) {
    this.dispatcher.dispatchPreparedAction(
      prepareDeleteUnit({
        unitId: unitId,
        course: course,
        planbook: planbook,
        rotationCalendar: rotationCalendar,
        shouldDeleteLessons: shouldDeleteLessons,
      }),
    )
  }

  /**
   * This appears to erase all lessons from the specified day of the planbook by locating the courseDate and related cardstacks
   * and resetting the cardstack attributes to null.
   * It does not reset the version history.
   */
  clearAllLessonsForDay(lessons: Array<CardStack>, dateString: string, planbookId: string) {
    this.dispatcher.dispatchPreparedAction(
      prepareClearAllLessonsForDay({
        lessons: lessons,
        dateString: dateString,
        planbookId: planbookId,
      }),
    )
  }

  /**
    This clears the lesson title and cards of text, but keeps version history intact
  */
  clearLesson(cardStack: CardStack) {
    this.dispatcher.dispatchPreparedAction(
      prepareLessonClear({
        cardStack: cardStack,
      }),
    )
  }

  deleteStandard(
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit,
    cardStackId: string,
    cardId: string,
    standard: CardStandard,
  ) {
    this._createLessonIfNeededAndCardsFromTemplate(modelsNeededForFirstEdit)
    this.dispatcher.dispatchAction(LESSON_STANDARDS_DELETE, {
      cardStackId: cardStackId,
      cardId: cardId,
      standard: standard,
    })
  }

  trackDownloadAttachment(cardStackId: string, attachment: CardAttachment) {
    this.dispatcher.dispatchPreparedAction(
      downloadAttachmentAction({
        cardStackId: cardStackId,
        attachment: attachment,
      }),
    )
  }

  renameAttachment(
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit,
    cardStackId: string,
    cardId: string,
    newTitle: string,
    attachmentId: string,
    attachments: Array<CardAttachment>,
  ) {
    this._createLessonIfNeededAndCardsFromTemplate(modelsNeededForFirstEdit)
    this.dispatcher.dispatchAction(LESSON_CARD_RENAME_ATTACHMENT, {
      cardStackId: cardStackId,
      sectionId: cardId,
      attachmentId: attachmentId,
      newTitle: newTitle,
      originalAttachments: attachments,
    })
  }

  removeAttachment(
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit,
    cardStackId: string,
    cardId: string,
    attachment: CardAttachment,
  ) {
    this._createLessonIfNeededAndCardsFromTemplate(modelsNeededForFirstEdit)
    this.dispatcher.dispatchAction(LESSON_CARD_REMOVE_ATTACHMENT, {
      cardStackId: cardStackId,
      sectionId: cardId,
      attachment: attachment,
    })
  }

  deleteCardComment(
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit,
    cardStackId: string,
    cardId: string,
    comment: Comment,
    commenterName: string,
  ) {
    this._createLessonIfNeededAndCardsFromTemplate(modelsNeededForFirstEdit)
    this.dispatcher.dispatchAction(LESSON_CARD_REMOVE_COMMENT, {
      cardStackId: cardStackId,
      cardId: cardId,
      comment,
      commenterName,
      deletedAt: moment().toISOString(),
    })
  }

  /**
   * Deletes a card comment on a UnifiedCardStack.
   *
   * Soft delete: merely sets the comment's deletedAt field. The commentId is not
   * removed from card.attrs or the lesson's extracted field.
   */
  deleteUnifiedCardComment(
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit,
    cardStackId: string,
    comment: Comment,
  ) {
    this._createLessonIfNeededAndCardsFromTemplate(modelsNeededForFirstEdit)
    this.dispatcher.dispatchPreparedAction(
      prepareLessonDeleteComment({
        cardStackId: cardStackId,
        ...lessonEditIds(modelsNeededForFirstEdit),
        comment,
        deletedAt: moment().toISOString(),
      }),
    )
  }

  removeLegacyComment(
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit,
    cardStackId: string,
    cardId: string,
    legacyComment: LegacyCardComment,
  ) {
    this._createLessonIfNeededAndCardsFromTemplate(modelsNeededForFirstEdit)
    this.dispatcher.dispatchAction(LESSON_CARD_REMOVE_LEGACY_COMMENT, {
      cardStackId: cardStackId,
      cardId: cardId,
      comment: legacyComment,
      deletedOn: moment().toISOString(),
    })
  }

  removeCard(modelsNeededForFirstEdit: ModelsNeededForFirstEdit, cardStackId: string, card: Card) {
    if (isNil(card) || isNil(card.id)) {
      console.log("No card", cardStackId, card)
      return
    }
    this._createLessonIfNeededAndCardsFromTemplate(modelsNeededForFirstEdit)
    this.dispatcher.dispatchAction(LESSON_CARD_DELETE, {
      cardStackId: cardStackId,
      card: cloneDeep(card),
    })
  }

  toggleStandardEmphasisLevel(
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit,
    cardStackId: string,
    cardId: string,
    emphasisLevel: CardStandard["emphasisLevel"],
    standards: Array<CardStandard> | null,
  ) {
    this._createLessonIfNeededAndCardsFromTemplate(modelsNeededForFirstEdit)

    this.dispatcher.dispatchAction(LESSON_STANDARDS_EMPHASIS, {
      cardStackId: cardStackId,
      cardId: cardId,
      standards: standards,
      emphasisLevel: emphasisLevel,
      previousStandards: cloneDeep(standards) || [],
    })
  }

  /**
   * Replaces all the standards in a card and then updates the planbook and course
   * to let them know what the most recent standard sets were
   */
  replaceStandards(
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit,
    cardStackId: string,
    cardId: string,
    standards: Array<CardStandard>,
    standardSetIds: Array<string> | null,
    previousStandards: Array<CardStandard>,
    planbookId: string | null,
    courseId: string | null,
    courseStandardSetIds: Array<string> | null,
  ) {
    this._createLessonIfNeededAndCardsFromTemplate(modelsNeededForFirstEdit)

    this.dispatcher.dispatchPreparedAction(
      lessonStandardsReplaceAction({
        cardStackId: cardStackId,
        cardId: cardId,
        standards: standards,
        previousStandards: previousStandards,
      }),
    )

    this.updateStandardSetIds(standardSetIds, planbookId, courseId, courseStandardSetIds)
  }

  /**
   * Tells the planbook and course what the most recent standard sets were.
   */
  updateStandardSetIds(
    standardSetIds: Array<string> | null,
    planbookId: string | null,
    courseId: string | null,
    courseStandardSetIds: Array<string> | null,
  ) {
    if (planbookId && standardSetIds) {
      this.dispatcher.dispatchAction(PLANBOOK_UPDATE_RECENT_STANDARD_SETS, {
        planbookId: planbookId,
        standardSetIds: standardSetIds,
      })
    }

    if (courseStandardSetIds && standardSetIds) {
      if (
        courseId &&
        standardSetIds &&
        (!courseStandardSetIds || !isEqual(sortBy(courseStandardSetIds), sortBy(standardSetIds)))
      ) {
        this.dispatcher.dispatchAction(COURSE_UPDATE_STANDARD_SET_IDS, {
          courseId: courseId,
          planbookId: planbookId,
          standardSetIds: standardSetIds,
        })
      }
    }
  }

  /*====================================================================================================
  PRIVATE
 ===================================================================================================*/

  /*----------------------------------------------------------------------------------------------------
 Create Lesson if needed methods
 ----------------------------------------------------------------------------------------------------*/

  /**
   * We have to check if it's the first edit, and if so, add the cards from the template
   */
  _createLessonIfNeededAndCardsFromTemplate(modelsNeededForFirstEdit: ModelsNeededForFirstEdit) {
    // if the first edit is false, check that we need to just save the cards
    if (this._createLessonIfNeeded(modelsNeededForFirstEdit, true) === false) {
      this._addCardsFromTemplate(modelsNeededForFirstEdit)
    }
  }

  /**
   * Used to determine wether we need to create this lesson and add it to the course
   */
  private _createLessonIfNeeded(
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit,
    setCards: boolean,
  ) {
    if (!isLessonModelsNeededForFirstEdit(modelsNeededForFirstEdit)) return false

    let isFirstEdit = modelsNeededForFirstEdit.cardStack.meta.isNewLesson === true

    if (isFirstEdit) {
      this.dispatcher.dispatchAction(LESSON_FIRST_EDIT, {
        rotationCalendar: modelsNeededForFirstEdit.rotationCalendar,
        planbook: modelsNeededForFirstEdit.planbook,
        course: modelsNeededForFirstEdit.course,
        dateString: modelsNeededForFirstEdit.dateString,
        cardStack: modelsNeededForFirstEdit.cardStack,
        setCards: setCards,
      })
    }

    return isFirstEdit
  }

  private _addCardsFromTemplate(modelsNeededForFirstEdit: ModelsNeededForFirstEdit) {
    if (!isLessonModelsNeededForFirstEdit(modelsNeededForFirstEdit)) return

    if (
      modelsNeededForFirstEdit.cardStack.meta.cardsUpdatingFromTemplate === true ||
      modelsNeededForFirstEdit.cardStack.attributes.cards === null
    ) {
      this.dispatcher.dispatchAction(LESSON_FIRST_CARDS_EDIT, {
        course: modelsNeededForFirstEdit.course,
        planbook: modelsNeededForFirstEdit.planbook,
        dateString: modelsNeededForFirstEdit.dateString,
        cardStack: modelsNeededForFirstEdit.cardStack,
      })
    }
  }

  /*----------------------------------------------------------------------------------------------------
  Intermediate Update Tasks
  ----------------------------------------------------------------------------------------------------*/
  _intermediateLessonTitleUpdate = task({ keepLatest: true }, async ({ cardStackId, value }) => {
    this.dispatcher.dispatchAction(LESSON_TITLE_INTERMEDIATE_UPDATE, {
      cardStackId: cardStackId,
      value: value,
    })
    await timeout(750)
  })

  _intermediateLessonCardUpdate = task(
    { keepLatest: true },
    async ({ id, prop, value, cardStackId }) => {
      this.dispatcher.dispatchAction(LESSON_CARD_INTERMEDIATE_UPDATE, {
        cardStackId: cardStackId,
        sectionId: id,
        prop: prop,
        value: value,
      })
      await timeout(750)
    },
  )

  /*----------------------------------------------------------------------------------------------------
  Move tasks
  ----------------------------------------------------------------------------------------------------*/

  private _moveWithinLesson(
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit,
    movedCard: Card,
    newCardStackId: string,
    cardsToMoveWithin: Array<Card>,
    newIndex: number,
  ) {
    // Remove old section
    let newCards = reject(cardsToMoveWithin, { id: movedCard.id })

    let movingCard = cloneDeep(movedCard)
    movingCard.attributes.position = null
    newCards.splice(newIndex, 0, movingCard)

    let newCardPositions = reorderPositionedList(newCards)
    if (newCardPositions.length === 0) return

    let positionDiffs = reject(newCardPositions, (tuple) => {
      let card = find(newCards, { id: tuple[0] })
      return card && card.attributes.position === tuple[1]
    })

    this._createLessonIfNeededAndCardsFromTemplate(modelsNeededForFirstEdit)
    this.dispatcher.dispatchAction(LESSON_CARD_MOVE_WITHIN_LESSON, {
      cardStackId: newCardStackId,
      positionDiffs: positionDiffs,
      sectionBeingMoved: movedCard,
    })
  }

  private _moveToNewLesson(
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit,
    movedCard: Card,
    oldCardStackId: string,
    newCardStackId: string,
    receiverContext,
    newCards: Array<Card>,
    newIndex: number,
  ) {
    let newPosition =
      newCards === null || newCards.length === 0
        ? 0
        : newIndex >= newCards.length
          ? // @ts-ignore:
            last(newCards).attributes.position + 10
          : // @ts-ignore:
            newCards[newIndex].attributes.position - 10

    let movingCard = cloneDeep(movedCard)
    movingCard.attributes.position = newPosition

    this._createLessonIfNeededAndCardsFromTemplate(modelsNeededForFirstEdit)
    this._createLessonIfNeededAndCardsFromTemplate(receiverContext.modelsNeededForFirstEdit)

    this.dispatcher.dispatchAction(LESSON_CARD_MOVE_TO_NEW_LESSON, {
      oldCardStackId: oldCardStackId,
      cardStackId: newCardStackId,
      card: movingCard,
      oldCard: movedCard,
    })
  }
}
